<template>
  <div>
    <div class="flex items-center justify-end mb-4">
      <div class="flex items-center gap-2">
        <input
          id="select-all-guidance-sources"
          type="checkbox"
          :checked="areAllGlobalSourcesSelected()"
          :indeterminate="areSomeGlobalSourcesSelected()"
          @change="toggleAllSources"
        />
        <label
          for="select-all-guidance-sources"
          class="text-sm font-medium text-primary-500 cursor-pointer"
        >
          All
        </label>
      </div>
    </div>
    <div
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-5 overflow-y-auto max-h-150 pr-2"
    >
      <template v-for="{ country, sources } in sourcesByCountry" :key="country.code">
        <component
          :is="showDetailedSelection ? 'div' : 'button'"
          class="space-y-2 border p-3 rounded-md transition-colors duration-100 w-full text-left"
          :class="[
            showDetailedSelection
              ? 'bg-gray-50/50'
              : areAllSourcesSelected(country.code)
                ? 'bg-blue-100 border-blue-300'
                : areSomeSourcesSelected(country.code)
                  ? 'bg-blue-50 border-blue-200'
                  : 'bg-gray-50/50 hover:bg-gray-100',
            !showDetailedSelection && 'cursor-pointer',
          ]"
          @click="!showDetailedSelection && toggleCountrySources(country.code)"
        >
          <div class="flex items-center gap-2">
            <input
              v-if="showDetailedSelection"
              :id="`guidance-country-${country.code}`"
              type="checkbox"
              class="rounded border-gray-300 text-blue-600"
              :checked="areAllSourcesSelected(country.code)"
              :indeterminate="areSomeSourcesSelected(country.code)"
              @change="toggleCountrySources(country.code)"
            />
            <label
              :for="showDetailedSelection ? `guidance-country-${country.code}` : undefined"
              class="flex items-center gap-2 font-medium text-gray-900 pointer-events-none"
              :class="{ 'cursor-pointer': showDetailedSelection }"
            >
              <img
                :src="country.flagUrl"
                :alt="`Flag of ${country.name}`"
                class="inline-block w-7 h-5 object-cover object-center"
              />
              {{ country.name }}
            </label>
          </div>
          <div v-if="showDetailedSelection" class="ml-6 space-y-1 max-h-50 overflow-y-auto pr-1">
            <template v-for="source in sources" :key="source.id">
              <div class="flex items-center gap-2">
                <input
                  :id="`guidance-source-${source.id}`"
                  type="checkbox"
                  class="rounded border-gray-300 text-blue-600"
                  :checked="selectedSources.includes(source.id)"
                  @change="toggleSource(source.id)"
                />
                <label
                  :for="`guidance-source-${source.id}`"
                  class="text-sm text-gray-700 cursor-pointer"
                >
                  {{ source.name }}
                </label>
              </div>
            </template>
          </div>
        </component>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { injectStrict } from "@app/utils/injectStrict";
import { GuidanceSourcesKey } from "@app/injectionKeys";
import { GuidanceSource } from "@core/application/guidance.service";

const { showDetailedSelection = false } = defineProps<{
  showDetailedSelection?: boolean;
}>();

const guidanceSources: GuidanceSource[] = injectStrict(GuidanceSourcesKey);

const selectedSources = defineModel<string[]>({ default: () => [] });

const sourcesByCountry = computed(() => {
  return guidanceSources.reduce(
    (acc, source) => {
      const countryCode = source.country.code;
      const country = {
        name: source.country.name,
        code: countryCode,
        flagUrl: source.country.flagUrl,
      };
      if (!acc[countryCode]) {
        acc[countryCode] = { sources: [], country };
      }
      acc[countryCode].sources.push(source);
      return acc;
    },
    {} as Record<
      string,
      {
        sources: GuidanceSource[];
        country: { name: string; code: string; flagUrl: string };
      }
    >,
  );
});

function toggleSource(sourceId: string) {
  const index = selectedSources.value.indexOf(sourceId);
  if (index > -1) {
    selectedSources.value.splice(index, 1);
  } else {
    selectedSources.value.push(sourceId);
  }
}

function toggleCountrySources(countryCode: string) {
  const countrySources = sourcesByCountry.value[countryCode]?.sources || [];
  const allSelected = countrySources.every((source) => selectedSources.value.includes(source.id));

  if (allSelected) {
    countrySources.forEach((source) => {
      const index = selectedSources.value.indexOf(source.id);
      if (index > -1) {
        selectedSources.value.splice(index, 1);
      }
    });
  } else {
    countrySources.forEach((source) => {
      if (!selectedSources.value.includes(source.id)) {
        selectedSources.value.push(source.id);
      }
    });
  }
}

function areAllSourcesSelected(countryCode: string): boolean {
  const countrySources = sourcesByCountry.value[countryCode]?.sources || [];
  return (
    countrySources.length > 0 &&
    countrySources.every((source) => selectedSources.value.includes(source.id))
  );
}

function areSomeSourcesSelected(countryCode: string): boolean {
  const countrySources = sourcesByCountry.value[countryCode]?.sources || [];
  const selectedCount = countrySources.filter((source) =>
    selectedSources.value.includes(source.id),
  ).length;
  return selectedCount > 0 && selectedCount < countrySources.length;
}

function areAllGlobalSourcesSelected(): boolean {
  return (
    guidanceSources.length > 0 &&
    guidanceSources.every((source) => selectedSources.value.includes(source.id))
  );
}

function areSomeGlobalSourcesSelected(): boolean {
  const selectedCount = guidanceSources.filter((source) =>
    selectedSources.value.includes(source.id),
  ).length;
  return selectedCount > 0 && selectedCount < guidanceSources.length;
}

function toggleAllSources() {
  if (areAllGlobalSourcesSelected()) {
    selectedSources.value = [];
  } else {
    selectedSources.value = guidanceSources.map((source) => source.id);
  }
}
</script>
